import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../utils/permissions';

type ManagementTab = 'records' | 'salary' | 'staff' | 'compensation' | 'holiday';
import html2canvas from 'html2canvas';
import { calculateSalaryDetailed, Holiday } from '../utils/salaryUtils';

const ROLE_OPTIONS = [
  { value: 'CSKH', label: 'Chăm sóc khách hàng' },
  { value: 'SX_AO', label: 'Sản xuất áo' },
  { value: 'SX_THE', label: 'Sản xuất thẻ' },
];

interface CheckInOutMedia {
  fileId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
}

interface CheckInOutRecord {
  id: number;
  staff_id: string;
  check_type: 'check_in' | 'check_out';
  check_time: string;
  location_latitude: string;
  location_longitude: string;
  location_address: string;
  created_at: string;
  staff_name: string;
  staff_role?: string;
  staff_color?: string;
  media?: CheckInOutMedia[];
}

interface Staff {
  id: string;
  name: string;
  status: 'Hoạt động' | 'Đã nghỉ';
  role?: string;
  color?: string;
  basic_salary?: number;
  extra_salary?: number;
  email?: string;
}

interface StaffSalaryInfo {
  staffId: string;
  staffName: string;
  staffRole: string | undefined;
  staffColor: string | undefined;
  basicSalary: number;
  extraSalary: number;
  totalShifts: number;
  totalHours: number;
  totalSalary: number;
  totalExtraHours: number;
  holidayDetails?: any[];
}

interface IssueRecord {
  id: string;
  staffId: string;
  staffName: string;
  staffColor: string | undefined;
  date: string;
  issueType: 'missing_checkout' | 'missing_checkin' | 'orphan_checkout';
  checkTime: string;
  description: string;
}

// Helper function to get role label from value
const getRoleLabel = (value: string | undefined): string => {
  if (!value) return '-';
  const option = ROLE_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

// Helper function to convert ISO datetime (YYYY-MM-DDTHH:mm) to DD-MM-YYYY HH:mm
const formatDateTimeForInput = (isoString: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return '';
  }
};

// Helper function to format currency with comma separator
const formatCurrency = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '0';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';
  return Math.round(amount).toLocaleString('vi-VN', { maximumFractionDigits: 0 });
};

// Helper function to get default date range
const getDefaultDateRange = () => {
  const today = new Date();
  const dayOfMonth = today.getDate();

  let startDate: Date;
  let endDate: Date = new Date(today);

  if (dayOfMonth < 6) {
    // If today < 6, startDate = first day of previous month, endDate = today
    startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  } else {
    // Otherwise, startDate = first day of current month, endDate = today
    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  }

  // Format as YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
};

export const ManagementCheckInOutPage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();

  const [records, setRecords] = useState<CheckInOutRecord[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<{
    checkType: 'check_in' | 'check_out';
    checkTime: string;
    dateParts?: { day: string; month: string; year: string; hour: string; minute: string };
  } | null>(null);
  const [selectedMediaRecord, setSelectedMediaRecord] = useState<CheckInOutRecord | null>(null);
  const [previewMediaUrl, setPreviewMediaUrl] = useState<{ url: string; type: 'image' | 'video'; name: string } | null>(null);
  const [filters, setFilters] = useState<{
    staffIds?: string[];
    startDate?: string;
    endDate?: string;
  }>(() => {
    // Initialize from URL params or use defaults
    const staffIds = searchParams.get('staffIds');
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const defaultDates = getDefaultDateRange();

    const initialFilters: {
      staffIds?: string[];
      startDate?: string;
      endDate?: string;
    } = {};

    if (staffIds) {
      initialFilters.staffIds = staffIds.split(',');
    }

    // Use URL params if available, otherwise use defaults
    initialFilters.startDate = urlStartDate || defaultDates.startDate;
    initialFilters.endDate = urlEndDate || defaultDates.endDate;

    return initialFilters;
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const staffDropdownRef = useRef<HTMLDivElement>(null);
  const [staffFilterSearch, setStaffFilterSearch] = useState('');
  const [activeTab, setActiveTab] = useState<ManagementTab>(() => {
    const tab = searchParams.get('tab');
    return (tab === 'records' || tab === 'salary' || tab === 'staff' || tab === 'compensation' || tab === 'holiday') ? tab : 'records';
  });
  const [salarySubTab, setSalarySubTab] = useState<'dashboard' | 'detail'>(() => {
    const subTab = searchParams.get('salarySubTab');
    return (subTab === 'dashboard' || subTab === 'detail') ? subTab : 'detail';
  });
  const [salarySortBy, setSalarySortBy] = useState<'hours' | 'salary'>(() => {
    const sortBy = searchParams.get('salarySortBy');
    return (sortBy === 'hours' || sortBy === 'salary') ? sortBy : 'salary';
  });
  const [salarySortOrder, setSalarySortOrder] = useState<'asc' | 'desc'>(() => {
    const sortOrder = searchParams.get('salarySortOrder');
    return (sortOrder === 'asc' || sortOrder === 'desc') ? sortOrder : 'desc';
  });
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [editingStaffData, setEditingStaffData] = useState<{
    name: string;
    role: string;
    basic_salary: string;
    extra_salary: string;
    color: string;
    status: 'Hoạt động' | 'Đã nghỉ';
    email: string;
  } | null>(null);
  const [salaryData, setSalaryData] = useState<StaffSalaryInfo[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [issues, setIssues] = useState<IssueRecord[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState<{
    staffId: string;
    checkType: 'check_in' | 'check_out';
    checkTime: string;
    dateParts: { day: string; month: string; year: string; hour: string; minute: string };
    address: string;
    latitude: string;
    longitude: string;
  }>(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear());
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return {
      staffId: '',
      checkType: 'check_in',
      checkTime: formatDateTimeForInput(now.toISOString()),
      dateParts: { day, month, year, hour: hours, minute: minutes },
      address: 'Duni',
      latitude: '0',
      longitude: '0'
    };
  });
  const [addStaffSearch, setAddStaffSearch] = useState('');
  const [showAddStaffDropdown, setShowAddStaffDropdown] = useState(false);
  const addStaffSearchRef = useRef<HTMLDivElement>(null);
  const recordsTableRef = useRef<HTMLDivElement>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [exportedImageData, setExportedImageData] = useState<string | null>(null);
  const [exportedFileName, setExportedFileName] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [copiedSalaryCardId, setCopiedSalaryCardId] = useState<string | null>(null);
  const [sendingEmailStaffId, setSendingEmailStaffId] = useState<string | null>(null);
  const [compensationRequests, setCompensationRequests] = useState<any[]>([]);
  const [compensationFilter, setCompensationFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayMultiplier, setNewHolidayMultiplier] = useState(1.5);
  const { user, can } = useAuth();
  // Mỗi tab cần quyền riêng; server vẫn kiểm tra lại từng API.
  const tabAllowed: Record<ManagementTab, boolean> = {
    records: can(PERMISSIONS.attendance, PERMISSIONS.partTimeSalary),
    salary: can(PERMISSIONS.partTimeSalary),
    staff: can(PERMISSIONS.employees),
    compensation: can(PERMISSIONS.attendance),
    holiday: can(PERMISSIONS.holidays),
  };
  const canLoadRecords = tabAllowed.records;

  useEffect(() => {
    if (!tabAllowed[activeTab]) {
      const firstAllowed = (Object.keys(tabAllowed) as ManagementTab[]).find((tab) => tabAllowed[tab]);
      if (firstAllowed) setActiveTab(firstAllowed);
    }
  }, [activeTab, tabAllowed.records, tabAllowed.salary, tabAllowed.staff, tabAllowed.compensation, tabAllowed.holiday]);

  const loadHolidays = async () => {
    try {
      const response = await fetch('/api/holidays');
      const data = await response.json();
      if (data.success) {
        setHolidays(data.data);
      }
    } catch (err) {
      console.error('Error loading holidays:', err);
    }
  };

  // Recalculate salary when holidays, staff, or records change
  useEffect(() => {
    if (records.length > 0) {
      calculateSalaryData(records);
    }
  }, [holidays, staff, records]);

  // Load staff list & holidays
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetch('/api/staff?all=true');
        const data = await response.json();
        if (data.success) {
          setStaff(data.data);
        }
      } catch (err) {
        console.error('Error loading staff:', err);
      }
    };
    loadStaff();
    loadHolidays();
  }, []);

  // Sync state from URL params (for browser back/forward)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const urlSalarySubTab = searchParams.get('salarySubTab');
    const urlSalarySortBy = searchParams.get('salarySortBy');
    const urlSalarySortOrder = searchParams.get('salarySortOrder');

    if (urlTab && (urlTab === 'records' || urlTab === 'salary' || urlTab === 'staff' || urlTab === 'compensation')) {
      if (activeTab !== urlTab) {
        setActiveTab(urlTab);
      }
    }

    const urlCompensationFilter = searchParams.get('compensationFilter');
    if (urlCompensationFilter && (urlCompensationFilter === 'pending' || urlCompensationFilter === 'approved' || urlCompensationFilter === 'rejected' || urlCompensationFilter === 'all')) {
      if (compensationFilter !== urlCompensationFilter) {
        setCompensationFilter(urlCompensationFilter);
      }
    }

    if (urlSalarySubTab && (urlSalarySubTab === 'dashboard' || urlSalarySubTab === 'detail')) {
      if (salarySubTab !== urlSalarySubTab) {
        setSalarySubTab(urlSalarySubTab);
      }
    }

    if (urlSalarySortBy && (urlSalarySortBy === 'hours' || urlSalarySortBy === 'salary')) {
      if (salarySortBy !== urlSalarySortBy) {
        setSalarySortBy(urlSalarySortBy);
      }
    }

    if (urlSalarySortOrder && (urlSalarySortOrder === 'asc' || urlSalarySortOrder === 'desc')) {
      if (salarySortOrder !== urlSalarySortOrder) {
        setSalarySortOrder(urlSalarySortOrder);
      }
    }
  }, [searchParams]);

  // Sync filters and tabs with URL
  useEffect(() => {
    const params = new URLSearchParams();

    // Filters
    if (filters.staffIds && filters.staffIds.length > 0) {
      params.set('staffIds', filters.staffIds.join(','));
    }
    if (filters.startDate) {
      params.set('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params.set('endDate', filters.endDate);
    }

    // Tabs
    if (activeTab !== 'records') {
      params.set('tab', activeTab);
    }
    if (activeTab === 'compensation' && compensationFilter !== 'pending') {
      params.set('compensationFilter', compensationFilter);
    }
    if (activeTab === 'salary' && salarySubTab !== 'detail') {
      params.set('salarySubTab', salarySubTab);
    }
    if (activeTab === 'salary' && salarySortBy !== 'salary') {
      params.set('salarySortBy', salarySortBy);
    }
    if (activeTab === 'salary' && salarySortOrder !== 'desc') {
      params.set('salarySortOrder', salarySortOrder);
    }

    // Only update URL if it's different from current params
    const currentStartDate = searchParams.get('startDate');
    const currentEndDate = searchParams.get('endDate');
    const currentStaffIds = searchParams.get('staffIds');
    const currentTab = searchParams.get('tab');
    const currentSalarySubTab = searchParams.get('salarySubTab');
    const currentSalarySortBy = searchParams.get('salarySortBy');
    const currentSalarySortOrder = searchParams.get('salarySortOrder');

    const shouldUpdate =
      filters.startDate !== currentStartDate ||
      filters.endDate !== currentEndDate ||
      (filters.staffIds?.join(',') || '') !== (currentStaffIds || '') ||
      activeTab !== (currentTab || 'records') ||
      (activeTab === 'salary' && salarySubTab !== (currentSalarySubTab || 'detail')) ||
      (activeTab === 'salary' && salarySortBy !== (currentSalarySortBy || 'salary')) ||
      (activeTab === 'salary' && salarySortOrder !== (currentSalarySortOrder || 'desc'));

    if (shouldUpdate) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, activeTab, salarySubTab, salarySortBy, salarySortOrder, setSearchParams, searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (staffDropdownRef.current && !staffDropdownRef.current.contains(event.target as Node)) {
        setShowStaffDropdown(false);
        setStaffFilterSearch('');
      }
      if (addStaffSearchRef.current && !addStaffSearchRef.current.contains(event.target as Node)) {
        setShowAddStaffDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newHolidayDate || newHolidayMultiplier === undefined) {
      alert('Vui lòng điền đầy đủ thông tin');
      return;
    }

    try {
      setLoading(true, 'Đang thêm ngày lễ...');
      const response = await fetch('/api/holidays', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          holiday_date: newHolidayDate,
          name: '',
          multiplier: newHolidayMultiplier
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewHolidayDate('');
        setNewHolidayMultiplier(1.5);
        await loadHolidays();
      } else {
        alert('Lỗi: ' + (data.error || 'Không xác định'));
      }
    } catch (err) {
      console.error('Error adding holiday:', err);
      alert('Lỗi kết nối server');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (id: string, dateStr: string) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa ngày lễ ngày ${dateStr} không?`)) {
      return;
    }

    try {
      setLoading(true, 'Đang xóa ngày lễ...');
      const response = await fetch(`/api/holidays/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        await loadHolidays();
      } else {
        alert('Lỗi: ' + (data.error || 'Không xác định'));
      }
    } catch (err) {
      console.error('Error deleting holiday:', err);
      alert('Lỗi kết nối server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'compensation' && tabAllowed.compensation) {
      loadCompensationRequests();
    }
  }, [activeTab, compensationFilter]);

  const loadData = async () => {
    if (!canLoadRecords) return;
    try {
      setLoading(true, 'Đang tải dữ liệu...');
      clearError();

      const queryParams = new URLSearchParams();
      if (filters.staffIds && filters.staffIds.length > 0) {
        filters.staffIds.forEach(id => queryParams.append('staffIds', id));
      }
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`/api/checkinout/all?${queryParams.toString()}`);
      const data = await response.json();

      if (data.success) {
        setRecords(data.data);
        // Calculate salary data
        calculateSalaryData(data.data);
        // Detect issues
        detectIssues(data.data);
      } else {
        setError(data.error || 'Lỗi khi tải dữ liệu');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const toggleStaffFilter = (staffId: string) => {
    const currentIds = filters.staffIds || [];
    const newIds = currentIds.includes(staffId)
      ? currentIds.filter(id => id !== staffId)
      : [...currentIds, staffId];

    if (newIds.length > 0) {
      setFilters({
        ...filters,
        staffIds: newIds
      });
    } else {
      const { staffIds, ...restFilters } = filters;
      setFilters(restFilters);
    }
  };

  const getTextColor = (bgColor: string | undefined): string => {
    if (!bgColor) return '#333';
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#333' : '#fff';
  };

  const handleEdit = (record: CheckInOutRecord) => {
    setEditingId(record.id);
    const date = new Date(record.check_time);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    setEditingData({
      checkType: record.check_type,
      checkTime: formatDateTimeForInput(record.check_time),
      dateParts: { day, month, year: String(year), hour: hours, minute: minutes }
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleSaveEdit = async (id: number) => {
    if (!editingData || !editingData.dateParts) return;

    try {
      setLoading(true, 'Đang cập nhật...');
      clearError();

      // Build ISO datetime from date parts
      const { day, month, year, hour, minute } = editingData.dateParts;
      const isoDateTime = `${year}-${month}-${day}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

      const response = await fetch(`/api/checkinout/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkType: editingData.checkType,
          checkTime: isoDateTime
        })
      });

      const data = await response.json();

      if (data.success) {
        await loadData();
        setEditingId(null);
        setEditingData(null);
        alert('Cập nhật thành công!');
      } else {
        setError(data.error || 'Lỗi khi cập nhật');
      }
    } catch (err) {
      console.error('Error updating record:', err);
      setError('Lỗi khi cập nhật');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa record này?')) {
      return;
    }

    try {
      setLoading(true, 'Đang xóa...');
      clearError();

      const response = await fetch(`/api/checkinout/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        await loadData();
        alert('Xóa thành công!');
      } else {
        setError(data.error || 'Lỗi khi xóa');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      setError('Lỗi khi xóa');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatDateTimeForInput(dateString);
  };

  const handleSort = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);

    const sorted = [...records].sort((a, b) => {
      const dateA = new Date(a.check_time).getTime();
      const dateB = new Date(b.check_time).getTime();
      return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    setRecords(sorted);
  };

  const calculateSalaryData = (records: CheckInOutRecord[]) => {
    // Group records by staff
    const staffMap = new Map<string, CheckInOutRecord[]>();

    records.forEach(record => {
      if (!staffMap.has(record.staff_id)) {
        staffMap.set(record.staff_id, []);
      }
      staffMap.get(record.staff_id)!.push(record);
    });

    const salaryInfoList: StaffSalaryInfo[] = [];

    staffMap.forEach((staffRecords, staffId) => {
      const firstRecord = staffRecords[0];
      const basicSalary = staff.find(s => s.id === firstRecord.staff_id)?.basic_salary || 0;
      const extraSalary = staff.find(s => s.id === firstRecord.staff_id)?.extra_salary || 0;

      const checkInCount = staffRecords.filter(r => r.check_type === 'check_in').length;
      const checkOutCount = staffRecords.filter(r => r.check_type === 'check_out').length;
      const shiftCount = Math.min(checkInCount, checkOutCount);

      const detailedSalary = calculateSalaryDetailed(
        staffRecords.map(r => ({ check_type: r.check_type, check_time: r.check_time })),
        basicSalary,
        extraSalary,
        holidays
      );

      salaryInfoList.push({
        staffId,
        staffName: firstRecord.staff_name,
        staffRole: firstRecord.staff_role,
        staffColor: firstRecord.staff_color,
        basicSalary,
        extraSalary,
        totalShifts: shiftCount,
        totalHours: detailedSalary.totalHours,
        totalSalary: detailedSalary.totalSalary,
        totalExtraHours: detailedSalary.totalExtraHours,
        holidayDetails: detailedSalary.details.filter(d => d.isHoliday)
      });
    });

    // Sort by total salary descending
    salaryInfoList.sort((a, b) => b.totalSalary - a.totalSalary);
    setSalaryData(salaryInfoList);
  };

  const detectIssues = (records: CheckInOutRecord[]) => {
    const issuesList: IssueRecord[] = [];

    // Group records by staff and date
    const staffDateMap = new Map<string, Map<string, CheckInOutRecord[]>>();

    records.forEach(record => {
      const date = new Date(record.check_time).toISOString().split('T')[0]; // YYYY-MM-DD

      if (!staffDateMap.has(record.staff_id)) {
        staffDateMap.set(record.staff_id, new Map());
      }

      const dateMap = staffDateMap.get(record.staff_id)!;
      if (!dateMap.has(date)) {
        dateMap.set(date, []);
      }

      dateMap.get(date)!.push(record);
    });

    // Check for issues in each staff-date combination
    staffDateMap.forEach((dateMap, staffId) => {
      const firstRecord = records.find(r => r.staff_id === staffId);
      if (!firstRecord) return;

      dateMap.forEach((dayRecords, date) => {
        // Count check-in and check-out
        let checkInCount = 0;
        let checkOutCount = 0;
        let lastCheckIn: CheckInOutRecord | null = null;
        let lastCheckOut: CheckInOutRecord | null = null;

        dayRecords.forEach(record => {
          if (record.check_type === 'check_in') {
            checkInCount++;
            lastCheckIn = record;
          } else {
            checkOutCount++;
            lastCheckOut = record;
          }
        });

        // Detect issues
        if (checkInCount > checkOutCount) {
          // Missing check-out: có check-in nhưng không có check-out tương ứng
          const missingCount = checkInCount - checkOutCount;
          const checkInRecord = lastCheckIn || dayRecords.find(r => r.check_type === 'check_in');
          if (checkInRecord) {
            issuesList.push({
              id: `missing_checkout_${staffId}_${date}_${checkInRecord.id}`,
              staffId,
              staffName: firstRecord.staff_name,
              staffColor: firstRecord.staff_color || undefined,
              date,
              issueType: 'missing_checkout',
              checkTime: checkInRecord.check_time,
              description: `Thiếu ${missingCount} check-out (có ${checkInCount} check-in, ${checkOutCount} check-out)`
            });
          }
        } else if (checkOutCount > checkInCount) {
          // Missing check-in or orphan check-out: có check-out nhưng không có check-in tương ứng
          const missingCount = checkOutCount - checkInCount;
          const checkOutRecord = lastCheckOut || dayRecords.find(r => r.check_type === 'check_out');
          if (checkOutRecord) {
            issuesList.push({
              id: `missing_checkin_${staffId}_${date}_${checkOutRecord.id}`,
              staffId,
              staffName: firstRecord.staff_name,
              staffColor: firstRecord.staff_color || undefined,
              date,
              issueType: 'orphan_checkout',
              checkTime: checkOutRecord.check_time,
              description: `Thiếu ${missingCount} check-in (có ${checkInCount} check-in, ${checkOutCount} check-out)`
            });
          }
        }
      });
    });

    // Sort by date descending, then by staff name
    issuesList.sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      return a.staffName.localeCompare(b.staffName);
    });

    setIssues(issuesList);
  };

  const handleEditStaff = (staff: Staff) => {
    setEditingStaffId(staff.id);
    setEditingStaffData({
      name: staff.name,
      role: staff.role || '',
      basic_salary: staff.basic_salary?.toString() || '',
      extra_salary: staff.extra_salary?.toString() || '',
      color: staff.color || '#3498db',
      status: staff.status,
      email: staff.email || ''
    });
  };

  const handleSaveStaff = async (staffId: string) => {
    if (!editingStaffData) return;

    try {
      setLoading(true, 'Đang cập nhật nhân viên...');
      clearError();

      const updateData: any = {
        name: editingStaffData.name,
        role: editingStaffData.role || null,
        color: editingStaffData.color,
        status: editingStaffData.status,
        email: editingStaffData.email.trim() || null
      };

      if (editingStaffData.basic_salary) {
        updateData.basic_salary = parseFloat(editingStaffData.basic_salary);
      }
      if (editingStaffData.extra_salary) {
        updateData.extra_salary = parseFloat(editingStaffData.extra_salary);
      }

      const response = await fetch(`/api/staff/${staffId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        // Reload staff list
        const staffResponse = await fetch('/api/staff?all=true');
        const staffData = await staffResponse.json();
        if (staffData.success) {
          setStaff(staffData.data);
        }
        setEditingStaffId(null);
        setEditingStaffData(null);
        alert('Cập nhật nhân viên thành công!');
      } else {
        setError(data.error || 'Lỗi khi cập nhật nhân viên');
      }
    } catch (err) {
      console.error('Error updating staff:', err);
      setError('Lỗi khi cập nhật nhân viên');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSalaryEmail = async (info: StaffSalaryInfo) => {
    const staffMember = staff.find(s => s.id === info.staffId);
    if (!staffMember || !staffMember.email) {
      alert(`Nhân viên ${info.staffName} chưa được thiết lập email. Vui lòng cập nhật email trong mục Quản lý nhân viên trước.`);
      return;
    }

    try {
      setSendingEmailStaffId(info.staffId);
      setLoading(true, 'Đang gửi email xác nhận lương...');
      clearError();

      const response = await fetch('/api/salary/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffId: info.staffId,
          startDate: filters.startDate,
          endDate: filters.endDate
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message || 'Gửi email xác nhận lương thành công!');
      } else {
        setError(data.error || 'Lỗi khi gửi email xác nhận lương');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Lỗi kết nối khi gửi email');
    } finally {
      setSendingEmailStaffId(null);
      setLoading(false);
    }
  };

  const handleQuickAddEmail = async (staffId: string, staffName: string) => {
    const staffMember = staff.find(s => s.id === staffId);
    const currentEmail = staffMember?.email || '';

    const newEmail = prompt(`Nhập email cho nhân viên ${staffName}:`, currentEmail);
    if (newEmail === null) return; // Hủy nhập

    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      alert('Email không được để trống!');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      alert('Định dạng email không hợp lệ!');
      return;
    }

    try {
      setLoading(true, 'Đang cập nhật email nhân viên...');
      clearError();

      const updateData = {
        name: staffMember?.name,
        role: staffMember?.role || null,
        color: staffMember?.color,
        status: staffMember?.status || 'Hoạt động',
        email: trimmedEmail
      };

      const response = await fetch(`/api/staff/${staffId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        // Tải lại danh sách nhân viên để cập nhật UI
        const staffResponse = await fetch('/api/staff?all=true');
        const staffData = await staffResponse.json();
        if (staffData.success) {
          setStaff(staffData.data);
        }
        alert(`Đã cập nhật email cho nhân viên ${staffName} thành công!`);
      } else {
        setError(data.error || 'Lỗi khi cập nhật email');
      }
    } catch (err) {
      console.error('Error quick updating email:', err);
      setError('Lỗi kết nối khi cập nhật email');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!addFormData.staffId) {
      setError('Vui lòng chọn nhân viên');
      return;
    }

    if (!addFormData.checkTime) {
      setError('Vui lòng chọn ngày giờ');
      return;
    }

    if (!addFormData.address || addFormData.address.trim() === '') {
      setError('Vui lòng nhập địa chỉ');
      return;
    }

    if (!addFormData.latitude || !addFormData.longitude) {
      setError('Vui lòng nhập tọa độ');
      return;
    }

    try {
      setLoading(true, 'Đang thêm ca điểm danh...');

      // Build ISO datetime from date parts
      const { day, month, year, hour, minute } = addFormData.dateParts;
      const isoDateTime = `${year}-${month}-${day}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

      const response = await fetch('/api/checkinout/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffId: addFormData.staffId,
          checkType: addFormData.checkType,
          latitude: parseFloat(addFormData.latitude),
          longitude: parseFloat(addFormData.longitude),
          address: addFormData.address.trim(),
          checkTime: isoDateTime
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Thêm ca điểm danh thành công!');
        setShowAddModal(false);
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = String(now.getFullYear());
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');

        setAddFormData({
          staffId: '',
          checkType: 'check_in',
          checkTime: formatDateTimeForInput(now.toISOString()),
          dateParts: { day, month, year, hour: hours, minute: minutes },
          address: 'Duni',
          latitude: '0',
          longitude: '0'
        });
        setAddStaffSearch('');
        await loadData();
      } else {
        setError(data.error || 'Lỗi khi thêm ca điểm danh');
      }
    } catch (err) {
      console.error('Error adding record:', err);
      setError('Lỗi khi thêm ca điểm danh');
    } finally {
      setLoading(false);
    }
  };

  const filteredStaffForAdd = staff.filter(s => {
    if (s.status !== 'Hoạt động') return false;
    if (!addStaffSearch.trim()) return true;
    const searchLower = addStaffSearch.toLowerCase();
    return s.name.toLowerCase().includes(searchLower) ||
      (s.role && s.role.toLowerCase().includes(searchLower));
  });

  const exportTableToPNG = async () => {
    if (!recordsTableRef.current) return;

    try {
      setLoading(true, 'Đang tạo ảnh PNG...');

      const canvas = await html2canvas(recordsTableRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: 1920,
        windowHeight: 1080
      });

      const imageData = canvas.toDataURL('image/png');
      const fileName = `danh-sach-diem-danh-${new Date().toISOString().split('T')[0]}.png`;

      setExportedImageData(imageData);
      setExportedFileName(fileName);
      setShowImagePreview(true);

      setLoading(false);
    } catch (err) {
      console.error('Error exporting to PNG:', err);
      setError('Lỗi khi xuất ảnh PNG');
      setLoading(false);
    }
  };

  const copyImageToClipboard = async () => {
    if (!exportedImageData) return;

    try {
      // Convert data URL to blob
      const response = await fetch(exportedImageData);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);

      // Show success state
      setCopySuccess(true);

      // Reset after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);

    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Lỗi khi sao chép ảnh vào clipboard');
    }
  };

  const downloadImage = () => {
    if (!exportedImageData || !exportedFileName) return;

    const link = document.createElement('a');
    link.download = exportedFileName;
    link.href = exportedImageData;
    link.click();
  };

  const loadCompensationRequests = async () => {
    try {
      setLoading(true, 'Đang tải yêu cầu điểm danh bù...');
      clearError();

      const queryParams = new URLSearchParams();
      if (compensationFilter !== 'all') {
        queryParams.append('status', compensationFilter);
      }

      const response = await fetch(`/api/checkinout/compensation?${queryParams.toString()}`);
      const data = await response.json();

      if (data.success) {
        setCompensationRequests(data.data);
      } else {
        setError(data.error || 'Lỗi khi tải yêu cầu điểm danh bù');
      }
    } catch (err) {
      console.error('Error loading compensation requests:', err);
      setError('Lỗi khi tải yêu cầu điểm danh bù');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewCompensation = async (id: number, status: 'approved' | 'rejected') => {
    if (!user?.id) {
      setError('Không tìm thấy thông tin người xét duyệt');
      return;
    }

    // Show confirmation dialog
    const confirmMessage = status === 'approved'
      ? 'Bạn có chắc chắn muốn phê duyệt yêu cầu điểm danh bù này?'
      : 'Bạn có chắc chắn muốn từ chối yêu cầu điểm danh bù này?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true, status === 'approved' ? 'Đang phê duyệt...' : 'Đang từ chối...');
      clearError();

      const response = await fetch(`/api/checkinout/compensation/${id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          reviewerId: user.id
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message || (status === 'approved' ? 'Đã phê duyệt thành công!' : 'Đã từ chối thành công!'));
        await loadCompensationRequests();
        // Reload main data to reflect new check-in/out if approved
        if (status === 'approved') {
          await loadData();
        }
      } else {
        setError(data.error || 'Có lỗi xảy ra');
      }
    } catch (err) {
      console.error('Error reviewing compensation request:', err);
      setError('Có lỗi xảy ra khi xét duyệt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="management-checkinout-page" style={{ padding: '20px' }}>
      <div className="container">
        <div className="header" style={{ marginBottom: '20px' }}>
          <h1><i className="fas fa-clipboard-list"></i> Quản Lý Điểm Danh</h1>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginTop: '20px',
            borderBottom: '2px solid #e2e8f0'
          }}>
            {tabAllowed.records && (
              <button
                onClick={() => setActiveTab('records')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'records' ? '#8d2a2c' : 'transparent',
                  color: activeTab === 'records' ? 'white' : '#475569',
                  border: 'none',
                  borderBottom: activeTab === 'records' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'records' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-list"></i> Danh sách điểm danh
              </button>
            )}
            {tabAllowed.salary && (
              <button
                onClick={() => setActiveTab('salary')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'salary' ? '#8d2a2c' : 'transparent',
                  color: activeTab === 'salary' ? 'white' : '#475569',
                  border: 'none',
                  borderBottom: activeTab === 'salary' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'salary' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-calculator"></i> Tính lương
              </button>
            )}
            {tabAllowed.staff && (
              <button
                onClick={() => setActiveTab('staff')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'staff' ? '#8d2a2c' : 'transparent',
                  color: activeTab === 'staff' ? 'white' : '#475569',
                  border: 'none',
                  borderBottom: activeTab === 'staff' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'staff' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-users"></i> Quản lý nhân viên
              </button>
            )}
            {tabAllowed.compensation && (
              <button
                onClick={() => setActiveTab('compensation')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'compensation' ? '#8d2a2c' : 'transparent',
                  color: activeTab === 'compensation' ? 'white' : '#475569',
                  border: 'none',
                  borderBottom: activeTab === 'compensation' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'compensation' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-check-circle"></i> Xét duyệt điểm danh bù
              </button>
            )}
            {tabAllowed.holiday && (
              <button
                onClick={() => setActiveTab('holiday')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'holiday' ? '#8d2a2c' : 'transparent',
                  color: activeTab === 'holiday' ? 'white' : '#475569',
                  border: 'none',
                  borderBottom: activeTab === 'holiday' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'holiday' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-calendar-day"></i> Quản lý ngày lễ
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginBottom: '15px' }}>Bộ lọc</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Từ ngày:</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value || '' })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Đến ngày:</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value || '' })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ position: 'relative' }} ref={staffDropdownRef}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Nhân viên:</label>
              <div
                onClick={() => {
                  setShowStaffDropdown(!showStaffDropdown);
                  if (showStaffDropdown) {
                    setStaffFilterSearch('');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: 'white',
                  minHeight: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px'
                }}
              >
                {filters.staffIds && filters.staffIds.length > 0 ? (
                  filters.staffIds.map(staffId => {
                    const staffMember = staff.find(s => s.id === staffId);
                    if (!staffMember) return null;
                    return (
                      <span
                        key={staffId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: staffMember.color || '#e9ecef',
                          color: getTextColor(staffMember.color),
                          fontWeight: '500'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStaffFilter(staffId);
                        }}
                      >
                        {staffMember.name}
                        <span style={{ marginLeft: '4px', cursor: 'pointer' }}>×</span>
                      </span>
                    );
                  })
                ) : (
                  <span style={{ color: '#999' }}>Chọn nhân viên...</span>
                )}
                <span style={{ marginLeft: 'auto', color: '#999' }}>
                  <i className={`fas fa-chevron-${showStaffDropdown ? 'up' : 'down'}`}></i>
                </span>
              </div>
              {showStaffDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  {/* Search Input */}
                  <div style={{ padding: '10px', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Tìm kiếm nhân viên..."
                        value={staffFilterSearch}
                        onChange={(e) => setStaffFilterSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '8px 30px 8px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      <i
                        className="fas fa-search"
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  </div>
                  {staff
                    .filter(s => {
                      if (!staffFilterSearch.trim()) return true;
                      const searchLower = staffFilterSearch.toLowerCase();
                      return s.name.toLowerCase().includes(searchLower) ||
                        (s.role && s.role.toLowerCase().includes(searchLower));
                    })
                    .map(s => (
                      <div
                        key={s.id}
                        onClick={() => toggleStaffFilter(s.id)}
                        style={{
                          padding: '10px 15px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: filters.staffIds?.includes(s.id) ? '#e3f2fd' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={filters.staffIds?.includes(s.id) || false}
                          onChange={() => { }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            background: s.color || '#e9ecef',
                            flexShrink: 0
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>{s.name}</div>
                          {s.role && (
                            <div style={{ fontSize: '12px', color: '#666' }}>{s.role}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  {staff.filter(s => {
                    if (!staffFilterSearch.trim()) return true;
                    const searchLower = staffFilterSearch.toLowerCase();
                    return s.name.toLowerCase().includes(searchLower) ||
                      (s.role && s.role.toLowerCase().includes(searchLower));
                  }).length === 0 && (
                      <div style={{ padding: '15px', textAlign: 'center', color: '#999' }}>
                        Không tìm thấy nhân viên
                      </div>
                    )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => setFilters({})}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-redo"></i> Reset
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#721c24'
          }}>
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {/* Salary Tab */}
        {activeTab === 'salary' && (
          <div>
            {/* Sub Tabs */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <button
                onClick={() => setSalarySubTab('detail')}
                style={{
                  padding: '10px 20px',
                  background: salarySubTab === 'detail' ? '#8d2a2c' : 'transparent',
                  color: salarySubTab === 'detail' ? 'white' : '#64748b',
                  border: 'none',
                  borderBottom: salarySubTab === 'detail' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: salarySubTab === 'detail' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-list"></i> Chi tiết
              </button>
              <button
                onClick={() => setSalarySubTab('dashboard')}
                style={{
                  padding: '10px 20px',
                  background: salarySubTab === 'dashboard' ? '#8d2a2c' : 'transparent',
                  color: salarySubTab === 'dashboard' ? 'white' : '#64748b',
                  border: 'none',
                  borderBottom: salarySubTab === 'dashboard' ? '3px solid #8d2a2c' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: salarySubTab === 'dashboard' ? '600' : '400',
                  transition: 'all 0.2s',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                <i className="fas fa-chart-bar"></i> Dashboard
              </button>
            </div>

            {/* Detail Sub Tab */}
            {salarySubTab === 'dashboard' && (
              <div>
                {salaryData.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6c757d',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.5 }}></i>
                    <div>Không có dữ liệu để tính lương</div>
                  </div>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '20px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          Tổng lương - Lương
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b' }}>
                          {formatCurrency(salaryData.reduce((sum, info) => sum + info.totalSalary, 0))} đ
                        </div>
                      </div>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          Tổng giờ làm
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b' }}>
                          {salaryData.reduce((sum, info) => sum + info.totalHours, 0).toFixed(2)}h
                        </div>
                      </div>
                    </div>

                    {/* Bar Chart */}
                    <div style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '20px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      maxHeight: 'calc(100vh - 400px)',
                      overflowY: 'auto'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>
                          Tổng lương
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <select
                            value={salarySortBy}
                            onChange={(e) => {
                              setSalarySortBy(e.target.value as 'hours' | 'salary');
                            }}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="salary">Sắp xếp theo lương</option>
                            <option value="hours">Sắp xếp theo giờ làm</option>
                          </select>
                          <button
                            onClick={() => setSalarySortOrder(salarySortOrder === 'asc' ? 'desc' : 'asc')}
                            style={{
                              padding: '6px 12px',
                              background: '#8d2a2c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {salarySortOrder === 'asc' ? '↑' : '↓'} {salarySortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}
                          </button>
                        </div>
                      </div>

                      {/* Legend */}
                      <div style={{
                        display: 'flex',
                        gap: '20px',
                        marginBottom: '15px',
                        fontSize: '13px',
                        color: '#64748b'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#3b82f6' }}></div>
                          <span>Thời gian làm</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#14b8a6' }}></div>
                          <span>Tổng lương</span>
                        </div>
                      </div>

                      {/* Bar Chart Items */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {[...salaryData]
                          .sort((a, b) => {
                            const valueA = salarySortBy === 'hours' ? a.totalHours : a.totalSalary;
                            const valueB = salarySortBy === 'hours' ? b.totalHours : b.totalSalary;
                            return salarySortOrder === 'asc' ? valueA - valueB : valueB - valueA;
                          })
                          .map((info) => {
                            const maxSalary = Math.max(...salaryData.map(s => s.totalSalary));
                            const maxHours = Math.max(...salaryData.map(s => s.totalHours));
                            const salaryWidth = maxSalary > 0 ? (info.totalSalary / maxSalary) * 100 : 0;
                            const hoursWidth = maxHours > 0 ? (info.totalHours / maxHours) * 100 : 0;

                            return (
                              <div key={info.staffId} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px',
                                padding: '10px',
                                borderRadius: '6px',
                                background: '#f8f9fa',
                                transition: 'background-color 0.2s'
                              }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f1f3f5';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                              >
                                <div style={{
                                  minWidth: '150px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#1e293b'
                                }}>
                                  {info.staffName}
                                </div>
                                <div style={{
                                  minWidth: '80px',
                                  fontSize: '13px',
                                  color: '#3b82f6',
                                  textAlign: 'right'
                                }}>
                                  {info.totalHours.toFixed(3)}
                                </div>
                                <div style={{
                                  flex: 1,
                                  position: 'relative',
                                  height: '24px',
                                  background: '#e2e8f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    position: 'absolute',
                                    left: 0,
                                    width: `${hoursWidth}%`,
                                    height: '100%',
                                    background: '#3b82f6',
                                    borderRadius: '4px',
                                    opacity: 0.3
                                  }}></div>
                                  <div style={{
                                    position: 'absolute',
                                    left: 0,
                                    width: `${salaryWidth}%`,
                                    height: '100%',
                                    background: '#14b8a6',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    paddingRight: '8px',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}>
                                    {salaryWidth > 15 && (
                                      <span>{formatCurrency(info.totalSalary)}</span>
                                    )}
                                  </div>
                                  {salaryWidth <= 15 && (
                                    <div style={{
                                      position: 'absolute',
                                      left: `${salaryWidth}%`,
                                      marginLeft: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: '#14b8a6',
                                      whiteSpace: 'nowrap',
                                      lineHeight: '24px'
                                    }}>
                                      {formatCurrency(info.totalSalary)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Dashboard Sub Tab */}
            {salarySubTab === 'detail' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '20px'
              }}>
                {salaryData.length === 0 ? (
                  <div style={{
                    gridColumn: '1 / -1',
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6c757d',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.5 }}></i>
                    <div>Không có dữ liệu để tính lương</div>
                  </div>
                ) : (
                  salaryData.map((info) => {
                    const cardRef = React.createRef<HTMLDivElement>();

                    const handleCopyCardAsImage = async () => {
                      if (!cardRef.current) return;

                      try {
                        // Temporarily remove hover effect and reset styles
                        const originalTransform = cardRef.current.style.transform;
                        const originalBoxShadow = cardRef.current.style.boxShadow;
                        cardRef.current.style.transform = 'translateY(0)';
                        cardRef.current.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';

                        // Ẩn các nút hành động để không dính vào ảnh
                        const actionsDiv = cardRef.current.querySelector('.card-actions') as HTMLElement;
                        const originalVisibility = actionsDiv?.style.visibility;
                        if (actionsDiv) actionsDiv.style.visibility = 'hidden';

                        // Capture the card as canvas
                        const canvas = await html2canvas(cardRef.current, {
                          backgroundColor: '#ffffff',
                          scale: 2, // Higher quality
                          logging: false,
                          useCORS: true
                        });

                        // Hiện lại các nút hành động
                        if (actionsDiv) actionsDiv.style.visibility = originalVisibility || 'visible';
                        // Restore original styles
                        cardRef.current.style.transform = originalTransform;
                        cardRef.current.style.boxShadow = originalBoxShadow;

                        // Convert canvas to blob
                        canvas.toBlob(async (blob) => {
                          if (!blob) return;

                          try {
                            // Try to copy to clipboard (modern browsers)
                            if (navigator.clipboard && window.ClipboardItem) {
                              const item = new ClipboardItem({ 'image/png': blob });
                              await navigator.clipboard.write([item]);
                              setCopiedSalaryCardId(info.staffId);
                              setTimeout(() => setCopiedSalaryCardId(null), 2000);
                            } else {
                              // Fallback: Download the image
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `salary_${info.staffName}_${new Date().getTime()}.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                              alert('Đã tải xuống ảnh!');
                            }
                          } catch (err) {
                            console.error('Error copying to clipboard:', err);
                            // Fallback to download
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `salary_${info.staffName}_${new Date().getTime()}.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                            alert('Đã tải xuống ảnh!');
                          }
                        }, 'image/png');
                      } catch (error) {
                        console.error('Error capturing card:', error);
                        alert('Có lỗi xảy ra khi chụp ảnh card!');
                      } finally {
                        // Luôn hiện lại các nút hành động nếu có lỗi trước đó
                        if (cardRef.current) {
                          const actionsDiv = cardRef.current.querySelector('.card-actions') as HTMLElement;
                          if (actionsDiv) actionsDiv.style.visibility = 'visible';
                        }
                      }
                    };

                    return (
                      <div
                        key={info.staffId}
                        ref={cardRef}
                        style={{
                          background: 'white',
                          borderRadius: '8px',
                          padding: '20px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          border: `2px solid ${info.staffColor || '#e2e8f0'}`,
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        }}
                      >
                        {/* Actions Container */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '6px',
                            zIndex: 10
                          }}
                          className="card-actions"
                        >
                          {/* Copy Button */}
                          <button
                            onClick={handleCopyCardAsImage}
                            style={{
                              background: copiedSalaryCardId === info.staffId ? '#22c55e' : '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.2s',
                              width: 'fit-content'
                            }}
                            onMouseEnter={(e) => {
                              if (copiedSalaryCardId !== info.staffId) {
                                e.currentTarget.style.background = '#2563eb';
                              }
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = copiedSalaryCardId === info.staffId ? '#22c55e' : '#3b82f6';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className={copiedSalaryCardId === info.staffId ? 'fas fa-check' : 'fas fa-camera'}></i>
                            {copiedSalaryCardId === info.staffId ? 'Đã copy' : 'Copy'}
                          </button>

                          {/* Email Action Button (Send Email or Add Email) */}
                          {(() => {
                            const staffMember = staff.find(s => s.id === info.staffId);
                            const hasEmail = !!staffMember?.email;

                            return hasEmail ? (
                              <button
                                onClick={() => handleSendSalaryEmail(info)}
                                disabled={sendingEmailStaffId === info.staffId}
                                style={{
                                  background: sendingEmailStaffId === info.staffId ? '#a7f3d0' : '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  cursor: sendingEmailStaffId === info.staffId ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s',
                                  width: 'fit-content'
                                }}
                                onMouseEnter={(e) => {
                                  if (sendingEmailStaffId !== info.staffId) {
                                    e.currentTarget.style.background = '#059669';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (sendingEmailStaffId !== info.staffId) {
                                    e.currentTarget.style.background = '#10b981';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }
                                }}
                              >
                                {sendingEmailStaffId === info.staffId ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Đang gửi...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-paper-plane"></i>
                                    Gửi mail
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleQuickAddEmail(info.staffId, info.staffName)}
                                style={{
                                  background: '#8d2a2c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s',
                                  width: 'fit-content'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#6f2022';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#8d2a2c';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fas fa-envelope"></i>
                                Thêm email
                              </button>
                            );
                          })()}
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '15px',
                          paddingBottom: '15px',
                          borderBottom: '1px solid #e2e8f0'
                        }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              background: info.staffColor || '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: getTextColor(info.staffColor),
                              fontWeight: '600',
                              fontSize: '16px',
                              marginRight: '12px'
                            }}
                          >
                            {info.staffName.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '16px', color: '#1e293b' }}>
                              {info.staffName}
                            </div>
                            {info.staffRole && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                {getRoleLabel(info.staffRole)}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '15px' }}>
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                              Giờ làm
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                              {info.totalHours}h
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                              Tăng ca
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                              {info.totalExtraHours}h
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                              Số ca làm
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '600', color: '#8d2a2c' }}>
                              {info.totalShifts}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>
                              Lương cơ bản
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '500', color: '#475569' }}>
                              {formatCurrency(info.basicSalary)} đ
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>
                              Phụ cấp tăng ca/giờ
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '500', color: '#475569' }}>
                              {formatCurrency(info.extraSalary)} đ
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                              Tổng lương
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>
                              {formatCurrency(info.totalSalary)} đ
                            </div>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', gridColumn: '1 / -1', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                            Lưu ý: Tổng lương = Lương cơ bản * Tổng giờ làm + Phụ cấp tăng ca * Tổng giờ tăng ca (áp dụng nhân hệ số đối với giờ làm ngày lễ).
                          </div>
                          {info.holidayDetails && info.holidayDetails.length > 0 && (
                            <div style={{ gridColumn: '1 / -1', marginTop: '10px', padding: '10px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#b45309', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="fas fa-gift"></i> Ngày lễ đã làm (được nhân hệ số):
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {info.holidayDetails.map((detail, idx) => {
                                  const d = new Date(detail.date);
                                  const dateFormatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                                  return (
                                    <div key={idx} style={{ fontSize: '11px', color: '#78350f', display: 'flex', justifyContent: 'space-between' }}>
                                      <span><strong>{dateFormatted}</strong>{detail.holidayName ? ` - ${detail.holidayName}` : ''}:</span>
                                      <span>{detail.hours}h {detail.extraHours > 0 ? `(+TC ${detail.extraHours}h)` : ''} <strong>(X{detail.multiplier})</strong> &rarr; <strong>{formatCurrency(detail.daySalary)}đ</strong></span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* Records Table */}
        {activeTab === 'records' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '7.5fr 2.5fr',
            gap: '20px',
            alignItems: 'start'
          }}>
            {/* Check-in/out Records Table */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflowX: 'auto'
            }} ref={recordsTableRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#1e293b' }}>
                  <i className="fas fa-list"></i> Danh sách điểm danh
                </h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={exportTableToPNG}
                    style={{
                      padding: '8px 16px',
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    title="Xuất bảng ra file PNG"
                  >
                    <i className="fas fa-download"></i> Xuất PNG
                  </button>
                  {can(PERMISSIONS.attendance) && (
                  <button
                    onClick={() => {
                      const now = new Date();
                      const day = String(now.getDate()).padStart(2, '0');
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const year = String(now.getFullYear());
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');

                      setAddFormData({
                        staffId: '',
                        checkType: 'check_in',
                        checkTime: formatDateTimeForInput(now.toISOString()),
                        dateParts: { day, month, year, hour: hours, minute: minutes },
                        address: 'Duni',
                        latitude: '0',
                        longitude: '0'
                      });
                      setAddStaffSearch('');
                      setShowAddModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <i className="fas fa-plus"></i> Thêm ca
                  </button>
                  )}
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={handleSort}
                      title={`Sắp xếp ${sortOrder === 'desc' ? 'tăng dần' : 'giảm dần'}`}
                    >
                      Thời gian
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {sortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Nhân viên</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Loại</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Địa chỉ</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Minh chứng</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                        Không có dữ liệu
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px' }}>
                          {editingId === record.id && editingData?.dateParts ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="text"
                                value={editingData.dateParts.day}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (editingData.dateParts) {
                                    setEditingData({
                                      ...editingData,
                                      dateParts: {
                                        day: value,
                                        month: editingData.dateParts.month,
                                        year: editingData.dateParts.year,
                                        hour: editingData.dateParts.hour,
                                        minute: editingData.dateParts.minute
                                      }
                                    });
                                  }
                                }}
                                placeholder="DD"
                                style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '35px', textAlign: 'center' }}
                              />
                              <span>-</span>
                              <input
                                type="text"
                                value={editingData.dateParts.month}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (editingData.dateParts) {
                                    setEditingData({
                                      ...editingData,
                                      dateParts: {
                                        day: editingData.dateParts.day,
                                        month: value,
                                        year: editingData.dateParts.year,
                                        hour: editingData.dateParts.hour,
                                        minute: editingData.dateParts.minute
                                      }
                                    });
                                  }
                                }}
                                placeholder="MM"
                                style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '35px', textAlign: 'center' }}
                              />
                              <span>-</span>
                              <input
                                type="text"
                                value={editingData.dateParts.year}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                  if (editingData.dateParts) {
                                    setEditingData({
                                      ...editingData,
                                      dateParts: {
                                        day: editingData.dateParts.day,
                                        month: editingData.dateParts.month,
                                        year: value,
                                        hour: editingData.dateParts.hour,
                                        minute: editingData.dateParts.minute
                                      }
                                    });
                                  }
                                }}
                                placeholder="YYYY"
                                style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '50px', textAlign: 'center' }}
                              />
                              <span style={{ marginLeft: '4px' }}></span>
                              <input
                                type="text"
                                value={editingData.dateParts.hour}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (editingData.dateParts) {
                                    setEditingData({
                                      ...editingData,
                                      dateParts: {
                                        day: editingData.dateParts.day,
                                        month: editingData.dateParts.month,
                                        year: editingData.dateParts.year,
                                        hour: value,
                                        minute: editingData.dateParts.minute
                                      }
                                    });
                                  }
                                }}
                                placeholder="HH"
                                style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '35px', textAlign: 'center' }}
                              />
                              <span>:</span>
                              <input
                                type="text"
                                value={editingData.dateParts.minute}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (editingData.dateParts) {
                                    setEditingData({
                                      ...editingData,
                                      dateParts: {
                                        day: editingData.dateParts.day,
                                        month: editingData.dateParts.month,
                                        year: editingData.dateParts.year,
                                        hour: editingData.dateParts.hour,
                                        minute: value
                                      }
                                    });
                                  }
                                }}
                                placeholder="mm"
                                style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '35px', textAlign: 'center' }}
                              />
                            </div>
                          ) : (
                            formatDateTime(record.check_time)
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: record.staff_color || '#e9ecef',
                              color: getTextColor(record.staff_color),
                              fontWeight: '500'
                            }}
                          >
                            {record.staff_name}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingId === record.id ? (
                            <select
                              value={editingData?.checkType || 'check_in'}
                              onChange={(e) => setEditingData({
                                ...editingData!,
                                checkType: e.target.value as 'check_in' | 'check_out'
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                            >
                              <option value="check_in">Check In</option>
                              <option value="check_out">Check Out</option>
                            </select>
                          ) : (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: record.check_type === 'check_in' ? '#d4edda' : '#f8d7da',
                              color: record.check_type === 'check_in' ? '#155724' : '#721c24'
                            }}>
                              {record.check_type === 'check_in' ? 'Check In' : 'Check Out'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px', maxWidth: '300px' }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {record.location_address}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {record.media && record.media.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => setSelectedMediaRecord(record)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '5px 10px',
                                background: '#f0fdf4',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 600
                              }}
                              title="Xem ảnh / video minh chứng"
                            >
                              <i className="fas fa-camera"></i>
                              <span>{record.media.length} tệp</span>
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Không có</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {editingId === record.id ? (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleSaveEdit(record.id)}
                                style={{
                                  padding: '5px 10px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Lưu"
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  padding: '5px 10px',
                                  background: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Hủy"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleEdit(record)}
                                style={{
                                  padding: '5px 10px',
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Chỉnh sửa"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(record.id)}
                                style={{
                                  padding: '5px 10px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Xóa"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Issues Table */}
            <div style={{
              background: 'white',
              padding: '15px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflowX: 'auto',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '12px', color: '#1e293b', fontSize: '16px' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i> Vấn đề
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', fontSize: '16px' }}>Ngày</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', fontSize: '16px' }}>Nhân viên</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ padding: '15px', textAlign: 'center', color: '#6c757d', fontSize: '12px' }}>
                        <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '20px', marginBottom: '8px', display: 'block' }}></i>
                        Không có vấn đề
                      </td>
                    </tr>
                  ) : (
                    issues.map((issue) => (
                      <tr key={issue.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '8px', fontSize: '13px' }}>
                          {new Date(issue.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              background: issue.staffColor || '#e9ecef',
                              color: getTextColor(issue.staffColor),
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            {issue.staffName}
                          </div>

                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '13px',
                            fontWeight: '500',
                            background: issue.issueType === 'missing_checkout' ? '#fff3cd' : '#f8d7da',
                            color: issue.issueType === 'missing_checkout' ? '#856404' : '#721c24',
                            display: 'inline-block',
                            marginLeft: '4px',
                            marginBottom: '4px'
                          }}>
                            {issue.issueType === 'missing_checkout' ? '⚠️ Thiếu OUT' : '⚠️ Thiếu IN'}
                          </span>

                          <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.3' }}>
                            {issue.description}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Staff Management Tab */}
        {activeTab === 'staff' && (() => {
          // Filter staff based on selected filters
          const filteredStaffForTable = filters.staffIds && filters.staffIds.length > 0
            ? staff.filter(s => filters.staffIds!.includes(s.id))
            : staff;

          return (
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflowX: 'auto'
            }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b' }}>
                <i className="fas fa-users"></i> Quản lý nhân viên
                {filters.staffIds && filters.staffIds.length > 0 && (
                  <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                    (Đang lọc: {filters.staffIds.length} nhân viên)
                  </span>
                )}
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Tên</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Vai trò</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Lương cơ bản/giờ</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Phụ cấp tăng ca/giờ</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Màu sắc</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Trạng thái</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaffForTable.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                        {filters.staffIds && filters.staffIds.length > 0
                          ? 'Không có nhân viên nào khớp với bộ lọc'
                          : 'Không có dữ liệu'}
                      </td>
                    </tr>
                  ) : (
                    filteredStaffForTable.map((s) => (
                      <tr key={s.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <input
                              type="text"
                              value={editingStaffData?.name || ''}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                name: e.target.value
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }}
                            />
                          ) : (
                            <div
                              style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                background: s.color || '#e9ecef',
                                color: getTextColor(s.color),
                                fontWeight: '500'
                              }}
                            >
                              {s.name}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <select
                              value={editingStaffData?.role || ''}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                role: e.target.value
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '180px' }}
                            >
                              <option value="">-- Chọn vai trò --</option>
                              {ROLE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            getRoleLabel(s.role)
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <input
                              type="email"
                              value={editingStaffData?.email || ''}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                email: e.target.value
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '180px' }}
                              placeholder="Nhập email..."
                            />
                          ) : (
                            s.email || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Chưa thiết lập</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <input
                              type="number"
                              step="1000"
                              value={editingStaffData?.basic_salary || ''}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                basic_salary: e.target.value
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '120px' }}
                              placeholder="0"
                            />
                          ) : (
                            s.basic_salary ? formatCurrency(s.basic_salary) + ' đ' : '-'
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <input
                              type="number"
                              step="1000"
                              value={editingStaffData?.extra_salary || ''}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                extra_salary: e.target.value
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px', width: '120px' }}
                              placeholder="0"
                            />
                          ) : (
                            s.extra_salary ? formatCurrency(s.extra_salary) + ' đ' : '-'
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <input
                              type="color"
                              value={editingStaffData?.color || '#3498db'}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                color: e.target.value
                              })}
                              style={{ padding: '2px', border: '1px solid #ddd', borderRadius: '4px', width: '60px', height: '32px', cursor: 'pointer' }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '4px',
                                background: s.color || '#e9ecef',
                                border: '1px solid #ddd'
                              }}
                            />
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {editingStaffId === s.id ? (
                            <select
                              value={editingStaffData?.status || 'Hoạt động'}
                              onChange={(e) => setEditingStaffData({
                                ...editingStaffData!,
                                status: e.target.value as 'Hoạt động' | 'Đã nghỉ'
                              })}
                              style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                            >
                              <option value="Hoạt động">Hoạt động</option>
                              <option value="Đã nghỉ">Đã nghỉ</option>
                            </select>
                          ) : (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: s.status === 'Hoạt động' ? '#d4edda' : '#f8d7da',
                              color: s.status === 'Hoạt động' ? '#155724' : '#721c24'
                            }}>
                              {s.status}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {editingStaffId === s.id ? (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleSaveStaff(s.id)}
                                style={{
                                  padding: '5px 10px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Lưu"
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingStaffId(null);
                                  setEditingStaffData(null);
                                }}
                                style={{
                                  padding: '5px 10px',
                                  background: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Hủy"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditStaff(s)}
                              style={{
                                padding: '5px 10px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="Chỉnh sửa"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Compensation Review Tab */}
        {activeTab === 'compensation' && (
          <div>
            {/* Filter */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: '500', marginRight: '10px' }}>Lọc theo trạng thái:</label>
                <button
                  onClick={() => setCompensationFilter('all')}
                  style={{
                    padding: '8px 16px',
                    background: compensationFilter === 'all' ? '#8d2a2c' : '#e2e8f0',
                    color: compensationFilter === 'all' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Tất cả
                </button>
                <button
                  onClick={() => setCompensationFilter('pending')}
                  style={{
                    padding: '8px 16px',
                    background: compensationFilter === 'pending' ? '#f59e0b' : '#e2e8f0',
                    color: compensationFilter === 'pending' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Chờ xét duyệt
                </button>
                <button
                  onClick={() => setCompensationFilter('approved')}
                  style={{
                    padding: '8px 16px',
                    background: compensationFilter === 'approved' ? '#10b981' : '#e2e8f0',
                    color: compensationFilter === 'approved' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Đã phê duyệt
                </button>
                <button
                  onClick={() => setCompensationFilter('rejected')}
                  style={{
                    padding: '8px 16px',
                    background: compensationFilter === 'rejected' ? '#ef4444' : '#e2e8f0',
                    color: compensationFilter === 'rejected' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Đã từ chối
                </button>
              </div>
            </div>

            {/* Requests Table */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflowX: 'auto'
            }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b' }}>
                <i className="fas fa-list"></i> Danh sách yêu cầu điểm danh bù
              </h3>
              {compensationRequests.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                  <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.5 }}></i>
                  <div>Không có yêu cầu nào</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Thời gian yêu cầu</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Nhân viên</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Loại</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Thời gian điểm danh</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Địa chỉ</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Lý do</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Trạng thái</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compensationRequests.map((request) => (
                      <tr key={request.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {new Date(request.created_at).toLocaleString('vi-VN')}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: request.staff_color || '#e9ecef',
                              color: getTextColor(request.staff_color),
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            {request.staff_name}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: request.check_type === 'check_in' ? '#d4edda' : '#f8d7da',
                            color: request.check_type === 'check_in' ? '#155724' : '#721c24'
                          }}>
                            {request.check_type === 'check_in' ? 'Check In' : 'Check Out'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {formatDateTime(request.check_time)}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {request.location_address}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px', maxWidth: '200px' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={request.reason}>
                            {request.reason}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: request.status === 'approved' ? '#d4edda' : request.status === 'rejected' ? '#f8d7da' : '#fff3cd',
                            color: request.status === 'approved' ? '#155724' : request.status === 'rejected' ? '#721c24' : '#856404'
                          }}>
                            {request.status === 'approved' ? 'Đã phê duyệt' : request.status === 'rejected' ? 'Đã từ chối' : 'Chờ xét duyệt'}
                          </span>
                          {request.reviewer_name && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                              Bởi: {request.reviewer_name}
                            </div>
                          )}
                          {request.review_comment && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                              "{request.review_comment}"
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {request.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleReviewCompensation(request.id, 'approved')}
                                style={{
                                  padding: '5px 10px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Phê duyệt"
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                onClick={() => handleReviewCompensation(request.id, 'rejected')}
                                style={{
                                  padding: '5px 10px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Từ chối"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Holiday Management Tab */}
        {activeTab === 'holiday' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
              {/* Form card */}
              <div style={{
                background: 'white',
                padding: '25px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: 'fit-content'
              }}>
                <h3 style={{ marginBottom: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-calendar-plus" style={{ color: '#4f46e5' }}></i> Thêm ngày lễ mới
                </h3>
                <form onSubmit={handleAddHoliday}>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                      Ngày lễ <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="date"
                      value={newHolidayDate}
                      onChange={(e) => setNewHolidayDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                      Hệ số nhân lương <span style={{ color: 'red' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <select
                        value={newHolidayMultiplier}
                        onChange={(e) => setNewHolidayMultiplier(parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      >
                        <option value={1.5}>X1.5</option>
                        <option value={2.0}>X2.0</option>
                        <option value={2.5}>X2.5</option>
                        <option value={3.0}>X3.0</option>
                        <option value={3.5}>X3.5</option>
                      </select>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        max="10"
                        value={newHolidayMultiplier}
                        onChange={(e) => setNewHolidayMultiplier(parseFloat(e.target.value) || 1.5)}
                        style={{
                          width: '70px',
                          padding: '10px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                  >
                    <i className="fas fa-plus"></i> Thêm ngày lễ
                  </button>
                </form>
              </div>

              {/* Table/List card */}
              <div style={{
                background: 'white',
                padding: '25px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                overflowX: 'auto'
              }}>
                <h3 style={{ marginBottom: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-calendar-alt" style={{ color: '#4f46e5' }}></i> Danh sách ngày lễ
                </h3>
                {holidays.length === 0 ? (
                  <div style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                    <i className="far fa-calendar" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
                    <div>Chưa cấu hình ngày lễ nào</div>
                    <div style={{ fontSize: '12px', marginTop: '5px' }}>Các ngày lễ được thêm sẽ tự động nhân hệ số lương khi tính toán.</div>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Ngày lễ</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#475569' }}>Hệ số nhân</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#475569' }}>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.map((h) => {
                        const d = new Date(h.holiday_date);
                        const dateFormatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                        return (
                          <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#334155' }}>
                              {dateFormatted}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                background: '#fef3c7',
                                color: '#d97706',
                                fontWeight: '700',
                                fontSize: '13px'
                              }}>
                                X{h.multiplier}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleDeleteHoliday(h.id!, dateFormatted)}
                                style={{
                                  padding: '6px 10px',
                                  background: '#fee2e2',
                                  color: '#ef4444',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#fecaca';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#fee2e2';
                                }}
                                title="Xóa ngày lễ"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Record Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'top',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddModal(false);
              }
            }}
          >
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '30px',
              marginTop: '15%',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#1e293b' }}>
                  <i className="fas fa-plus-circle"></i> Thêm ca điểm danh nhanh
                </h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999'
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleAddRecord}>
                {/* Staff Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    <i className="fas fa-user"></i> Chọn Nhân Viên <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div
                    ref={addStaffSearchRef}
                    style={{ position: 'relative', width: '100%' }}
                  >
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Tìm kiếm nhân viên (tên hoặc vai trò)..."
                        value={addStaffSearch}
                        onChange={(e) => {
                          setAddStaffSearch(e.target.value);
                          setShowAddStaffDropdown(true);
                        }}
                        onFocus={() => setShowAddStaffDropdown(true)}
                        style={{
                          width: '100%',
                          padding: '12px 40px 12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      <i
                        className="fas fa-search"
                        style={{
                          position: 'absolute',
                          right: '15px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>

                    {showAddStaffDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #ddd',
                          borderTop: 'none',
                          borderRadius: '0 0 4px 4px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        {filteredStaffForAdd.length > 0 ? (
                          filteredStaffForAdd.map(s => (
                            <div
                              key={s.id}
                              onClick={() => {
                                setAddFormData({ ...addFormData, staffId: s.id });
                                setAddStaffSearch(s.name);
                                setShowAddStaffDropdown(false);
                              }}
                              style={{
                                padding: '12px 15px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: addFormData.staffId === s.id ? '#e3f2fd' : 'white',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (addFormData.staffId !== s.id) {
                                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (addFormData.staffId !== s.id) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              <div style={{ fontWeight: '500', color: '#333' }}>
                                {s.name}
                              </div>
                              {s.role && (
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                  {s.role}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '15px', textAlign: 'center', color: '#999' }}>
                            Không tìm thấy nhân viên
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Check Type Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    <i className="fas fa-toggle-on"></i> Loại Điểm Danh <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <label style={{
                      flex: 1,
                      padding: '15px',
                      border: `2px solid ${addFormData.checkType === 'check_in' ? '#28a745' : '#ddd'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: addFormData.checkType === 'check_in' ? '#e8f5e9' : 'white',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="radio"
                        name="checkType"
                        value="check_in"
                        checked={addFormData.checkType === 'check_in'}
                        onChange={(e) => setAddFormData({ ...addFormData, checkType: e.target.value as 'check_in' | 'check_out' })}
                        style={{ marginRight: '8px' }}
                      />
                      <i className="fas fa-sign-in-alt" style={{ color: '#28a745', marginRight: '8px' }}></i>
                      Check In
                    </label>
                    <label style={{
                      flex: 1,
                      padding: '15px',
                      border: `2px solid ${addFormData.checkType === 'check_out' ? '#dc3545' : '#ddd'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: addFormData.checkType === 'check_out' ? '#ffebee' : 'white',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="radio"
                        name="checkType"
                        value="check_out"
                        checked={addFormData.checkType === 'check_out'}
                        onChange={(e) => setAddFormData({ ...addFormData, checkType: e.target.value as 'check_in' | 'check_out' })}
                        style={{ marginRight: '8px' }}
                      />
                      <i className="fas fa-sign-out-alt" style={{ color: '#dc3545', marginRight: '8px' }}></i>
                      Check Out
                    </label>
                  </div>
                </div>

                {/* Date Time Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    <i className="fas fa-calendar-alt"></i> Ngày Giờ <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="text"
                        value={addFormData.dateParts.day}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setAddFormData({
                            ...addFormData,
                            dateParts: {
                              day: value,
                              month: addFormData.dateParts.month,
                              year: addFormData.dateParts.year,
                              hour: addFormData.dateParts.hour,
                              minute: addFormData.dateParts.minute
                            }
                          });
                        }}
                        placeholder="DD"
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '60px',
                          textAlign: 'center'
                        }}
                        required
                      />
                      <span style={{ fontSize: '16px', fontWeight: '500' }}>-</span>
                      <input
                        type="text"
                        value={addFormData.dateParts.month}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setAddFormData({
                            ...addFormData,
                            dateParts: {
                              day: addFormData.dateParts.day,
                              month: value,
                              year: addFormData.dateParts.year,
                              hour: addFormData.dateParts.hour,
                              minute: addFormData.dateParts.minute
                            }
                          });
                        }}
                        placeholder="MM"
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '60px',
                          textAlign: 'center'
                        }}
                        required
                      />
                      <span style={{ fontSize: '16px', fontWeight: '500' }}>-</span>
                      <input
                        type="text"
                        value={addFormData.dateParts.year}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setAddFormData({
                            ...addFormData,
                            dateParts: {
                              day: addFormData.dateParts.day,
                              month: addFormData.dateParts.month,
                              year: value,
                              hour: addFormData.dateParts.hour,
                              minute: addFormData.dateParts.minute
                            }
                          });
                        }}
                        placeholder="YYYY"
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '80px',
                          textAlign: 'center'
                        }}
                        required
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                      <input
                        type="text"
                        value={addFormData.dateParts.hour}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setAddFormData({
                            ...addFormData,
                            dateParts: {
                              day: addFormData.dateParts.day,
                              month: addFormData.dateParts.month,
                              year: addFormData.dateParts.year,
                              hour: value,
                              minute: addFormData.dateParts.minute
                            }
                          });
                        }}
                        placeholder="HH"
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '60px',
                          textAlign: 'center'
                        }}
                        required
                      />
                      <span style={{ fontSize: '16px', fontWeight: '500' }}>:</span>
                      <input
                        type="text"
                        value={addFormData.dateParts.minute}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setAddFormData({
                            ...addFormData,
                            dateParts: {
                              day: addFormData.dateParts.day,
                              month: addFormData.dateParts.month,
                              year: addFormData.dateParts.year,
                              hour: addFormData.dateParts.hour,
                              minute: value
                            }
                          });
                        }}
                        placeholder="mm"
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '60px',
                          textAlign: 'center'
                        }}
                        required
                      />
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Định dạng: DD-MM-YYYY HH:mm (24 giờ)
                  </div>
                </div>

                {/* Address */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    <i className="fas fa-map-marker-alt"></i> Địa chỉ <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={addFormData.address}
                    onChange={(e) => setAddFormData({ ...addFormData, address: e.target.value })}
                    placeholder="Nhập địa chỉ..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>

                {/* Coordinates */}
                <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      <i className="fas fa-map-pin"></i> Latitude <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={addFormData.latitude}
                      onChange={(e) => setAddFormData({ ...addFormData, latitude: e.target.value })}
                      placeholder="10.762622"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      <i className="fas fa-map-pin"></i> Longitude <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={addFormData.longitude}
                      onChange={(e) => setAddFormData({ ...addFormData, longitude: e.target.value })}
                      placeholder="106.660172"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      required
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div style={{
                    padding: '12px',
                    background: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    color: '#721c24'
                  }}>
                    <i className="fas fa-exclamation-circle"></i> {error}
                  </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      const now = new Date();
                      const day = String(now.getDate()).padStart(2, '0');
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const year = String(now.getFullYear());
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');

                      setAddFormData({
                        staffId: '',
                        checkType: 'check_in',
                        checkTime: formatDateTimeForInput(now.toISOString()),
                        dateParts: { day, month, year, hour: hours, minute: minutes },
                        address: 'Duni',
                        latitude: '0',
                        longitude: '0'
                      });
                      setAddStaffSearch('');
                      clearError();
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-save"></i> Thêm
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && exportedImageData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              zIndex: 3000,
              padding: '20px',
              paddingTop: '100px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowImagePreview(false);
                setCopySuccess(false);
              }
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '90vw',
                maxHeight: 'calc(100vh - 120px)',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, color: '#1e293b' }}>
                  <i className="fas fa-image"></i> Xem trước ảnh
                </h2>
                <button
                  onClick={() => {
                    setShowImagePreview(false);
                    setCopySuccess(false);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  overflow: 'auto',
                  maxHeight: 'calc(100vh - 320px)',
                  marginBottom: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: '#f8f9fa',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '10px'
                }}
              >
                <img
                  src={exportedImageData}
                  alt="Exported table"
                  style={{
                    width: '50%',
                    height: '100%',
                    display: 'block'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={copyImageToClipboard}
                  style={{
                    padding: '10px 20px',
                    background: copySuccess ? '#28a745' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <i className={copySuccess ? 'fas fa-check' : 'fas fa-copy'}></i> {copySuccess ? 'Đã sao chép' : 'Sao chép'}
                </button>
                <button
                  onClick={downloadImage}
                  style={{
                    padding: '10px 20px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-download"></i> Tải xuống
                </button>
                <button
                  onClick={() => {
                    setShowImagePreview(false);
                    setCopySuccess(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Xem Minh Chứng Ảnh / Video Điểm Danh */}
        {selectedMediaRecord && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setSelectedMediaRecord(null)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '720px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-images" style={{ color: '#0284c7' }}></i>
                    Minh chứng điểm danh: {selectedMediaRecord.staff_name}
                  </h3>
                  <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '4px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: selectedMediaRecord.check_type === 'check_in' ? '#dcfce7' : '#fee2e2',
                      color: selectedMediaRecord.check_type === 'check_in' ? '#15803d' : '#b91c1c',
                      fontWeight: 600,
                      marginRight: '6px'
                    }}>
                      {selectedMediaRecord.check_type === 'check_in' ? 'Check In' : 'Check Out'}
                    </span>
                    {formatDateTime(selectedMediaRecord.check_time)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMediaRecord(null)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#e2e8f0',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    fontSize: '14px'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '20px', overflowY: 'auto' }}>
                <div style={{ marginBottom: '14px', fontSize: '13px', color: '#475569' }}>
                  <strong>Địa chỉ:</strong> {selectedMediaRecord.location_address || 'Chưa có thông tin địa chỉ'}
                </div>

                {selectedMediaRecord.media && selectedMediaRecord.media.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '14px'
                  }}>
                    {selectedMediaRecord.media.map((med, i) => {
                      const isVid = med.mimeType?.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(med.fileName || med.originalName || '');
                      return (
                        <div
                          key={med.fileId || i}
                          style={{
                            background: '#0f172a',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            position: 'relative',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                          }}
                        >
                          {isVid ? (
                            <video
                              src={med.url}
                              controls
                              playsInline
                              style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <img
                              src={med.url}
                              alt={med.originalName || `Ảnh ${i + 1}`}
                              onClick={() => setPreviewMediaUrl({ url: med.url, type: 'image', name: med.originalName || `Ảnh ${i + 1}` })}
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer'
                              }}
                            />
                          )}
                          <div style={{
                            padding: '6px 10px',
                            background: 'white',
                            borderTop: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '11.5px',
                            color: '#64748b'
                          }}>
                            <span style={{ fontWeight: 600 }}>{isVid ? '🎥 Video' : '📷 Ảnh'} #{i + 1}</span>
                            <a
                              href={med.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#0284c7', textDecoration: 'none', fontWeight: 500 }}
                            >
                              Mở tab mới ↗
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                    Bản ghi này chưa có ảnh hoặc video minh chứng
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setSelectedMediaRecord(null)}
                  style={{
                    padding: '8px 18px',
                    background: '#0f172a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lightbox Modal phóng to ảnh */}
        {previewMediaUrl && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.88)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setPreviewMediaUrl(null)}
          >
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '88vh' }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setPreviewMediaUrl(null)}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '0',
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '28px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
              <img
                src={previewMediaUrl.url}
                alt={previewMediaUrl.name}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  display: 'block',
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
